/* Angular Component */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  HostBinding,
} from "@angular/core";
import moment from "moment";
import { SearchService } from "../../../services/search.service";
import { TagRepository } from "../../../domain/tag-repository";
import { UserService } from "../../../domain/user-service";
import { CountryService } from "../../../domain/country-service";
import { from, Observable, Subscription } from "rxjs";
import { HttpService } from "../../../infrastructure/http/http";
import { Logger } from "../../../infrastructure/logger";
import { GraphicsRepository } from "../../../domain/graphics-repository";
import { UI } from "../../../services/ui.service";
import { SchedulerAsideService } from "../schedulerAside/scheduler-aside-service";
import { TenantRepository } from "../../../domain/tenant-repository";
import { MediaRepository } from "../../../domain/media-repository";
import { UploadService } from "../../../infrastructure/upload/upload-service";

@Component({
  selector: "filter",
  templateUrl: "./filter.component.html",
  styleUrls: ["./filter.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterComponent implements OnInit, OnDestroy {
  ascendingOrderMedia: boolean = true;
  ascendingOrderGraphics: boolean = true;

  orderByMedia = 0;
  orderByGraphics = 0;

  tags = [];
  assetTypes = [];
  languages: [{ code: string; name: string }];
  countries = [];
  templates = [];

  sortByMedia = "Name";
  sortByGraphics = "Name";
  statusChosen = "All";

  startDateCreatedMedia: moment.Moment;
  endDateCreatedMedia: moment.Moment;

  startDateCreatedGraphics: moment.Moment;
  endDateCreatedGraphics: moment.Moment;

  startDateLicense: moment.Moment;
  endDateLicense: moment.Moment;

  showMoreTags: boolean = false;
  showMoreAssets: boolean = false;
  showMoreLanguages: boolean = false;
  showMoreCountries: boolean = false;
  showMoreTemplates: boolean = false;

  checkAllTags: boolean = true;
  checkAllAssets: boolean = true;
  checkAllLanguages: boolean = true;
  checkAllCountries: boolean = true;
  checkAllTemplates: boolean = true;

  searchMode = "video";

  @HostBinding("class.isHidden") isHidden: boolean = false;

  tagCreatedSubscription: Subscription;
  mamCollapsedSubscription: Subscription;
  schedulerAsideSubscription: Subscription;
  linkedFileUpdated: Subscription;
  Ob: Observable<any>;
  private searchModeSubscription: Subscription;
  constructor(
    private searchService: SearchService,
    private tagRepository: TagRepository,
    private countryService: CountryService,
    private logger: Logger,
    private changeDetectorRef: ChangeDetectorRef,
    private graphicsRepository: GraphicsRepository,
    private ui: UI,
    private mediaRepository: MediaRepository,
    private schedulerAsideService: SchedulerAsideService,
  ) {}

  ngOnInit() {
    this.schedulerAsideSubscription =
      this.schedulerAsideService.onCollapsed.subscribe((isCollapsed) => {
        this.isHidden = !!isCollapsed;
        this.changeDetectorRef.markForCheck();
      });
    this.linkedFileUpdated =
      this.mediaRepository.mediaAssociatedFilesUpdated.subscribe(() => {
        this.updateTenantMediaLanguages();
      });
    /* Subscribing Mam Event */
    this.mamCollapsedSubscription = this.ui.EVENTS.MamCollapsed.subscribe(
      (value) => {
        this.isHidden = !!value;
        this.changeDetectorRef.markForCheck();
      },
    );

    this.Ob = from(this.searchService.searchServiceMode);
    this.searchModeSubscription = this.Ob.subscribe(() => {
      this.searchMode = this.searchService.searchMode;
      this.changeDetectorRef.markForCheck();
    });

    this.tags = this.tagRepository.getGeneralTags();
    this.assetTypes = this.tagRepository.getAssetTypeTags();
    this.countries = this.countryService.countries;
    this.graphicsRepository
      .getTemplatesNamesAndIds()
      .then((response) => {
        this.templates = response;
        this.changeDetectorRef.markForCheck();
      })
      .catch((err) => {
        this.logger.error(
          "couldn't get graphics templates names and ids " + err,
        );
      });

    this.tagCreatedSubscription =
      this.tagRepository.generalTagCreated.subscribe((tag) => {
        this.tags.push(tag);
        this.changeDetectorRef.markForCheck();
      });

    this.updateTenantMediaLanguages();
  }

  updateTenantMediaLanguages() {
    this.mediaRepository.getTenantMediasLanguages().then((result) => {
      this.languages = result.length > 0 ? result : this.languages;
      this.changeDetectorRef.markForCheck();
    });
  }

  changeStartDateCreated(val) {
    if (this.searchMode === "video") {
      this.startDateCreatedMedia = val;
      this.searchService.startDateCreatedMedia = val;
    } else {
      this.startDateCreatedGraphics = val;
      this.searchService.startDateCreatedGraphics = val;
    }

    this.searchService.subjectNext();
  }

  changeEndDateCreated(val) {
    if (this.searchMode === "video") {
      this.endDateCreatedMedia = val;
      this.searchService.endDateCreatedMedia = val;
    } else {
      this.endDateCreatedGraphics = val;
      this.searchService.endDateCreatedGraphics = val;
    }
    this.searchService.subjectNext();
  }

  changeStartDateLicense(val) {
    this.startDateLicense = val;
    this.searchService.startDateLicense = val;
    this.searchService.subjectNext();
  }

  changeEndDateLicense(val) {
    this.endDateLicense = val;
    this.searchService.endDateLicense = val;
    this.searchService.subjectNext();
  }

  onOrderByClick() {
    if (this.searchMode === "video") {
      this.ascendingOrderMedia = !this.ascendingOrderMedia;
      this.orderByMedia = this.orderByMedia === 0 ? 1 : 0;
      this.searchService.orderByMedia = this.orderByMedia;
    } else {
      this.ascendingOrderGraphics = !this.ascendingOrderGraphics;
      this.orderByGraphics = this.orderByGraphics === 0 ? 1 : 0;
      this.searchService.orderByGraphics = this.orderByGraphics;
    }
    this.searchService.subjectNext();
  }

  onClickSortByOption(val, sortBy) {
    if (this.searchMode === "video") {
      this.sortByMedia = sortBy;
      this.searchService.sortByMedia = this.sortByMedia;
    } else {
      this.sortByGraphics = sortBy;
      this.searchService.sortByGraphics = this.sortByGraphics;
    }
    this.searchService.subjectNext();
  }

  onClearAllFilters() {
    if (this.searchMode === "video") {
      this.searchService.statusChosen = "All";
      this.searchService.tags = [];
      this.searchService.countries = [];
      this.searchService.languages = [];
      this.searchService.assetTypes = [];
      this.searchService.startDateCreatedMedia = null;
      this.searchService.endDateCreatedMedia = null;
      this.searchService.startDateLicense = null;
      this.searchService.endDateLicense = null;

      this.statusChosen = "All";
      this.checkAllTags = true;
      this.checkAllAssets = true;
      this.checkAllLanguages = true;
      this.checkAllCountries = true;

      this.startDateCreatedMedia = null;
      this.endDateCreatedMedia = null;
      this.startDateLicense = null;
      this.endDateLicense = null;
      this.searchService.subjectNext();
    } else {
      this.searchService.templates = [] as unknown as [
        { id: number; name: string },
      ];
      this.searchService.startDateCreatedGraphics = null;
      this.searchService.endDateCreatedGraphics = null;
      this.startDateCreatedGraphics = null;
      this.endDateCreatedGraphics = null;
      this.searchService.subjectNext();
    }
  }

  isSortOptionChecked(sortBy: string) {
    if (this.searchMode === "video") {
      if (this.sortByMedia === sortBy) {
        return true;
      }
      return false;
    } else {
      if (this.sortByGraphics === sortBy) {
        return true;
      }
      return false;
    }
  }

  isStatusChosen(status: string) {
    if (this.statusChosen === status) {
      return true;
    }
    return false;
  }

  onClickStatusByOption(val, status) {
    this.statusChosen = status;
    this.searchService.statusChosen = this.statusChosen;
    this.searchService.subjectNext();
  }

  onClickTag(val, index) {
    if (val.target.checked) {
      this.searchService.tags.push(this.tags[index]);
      this.checkAllTags = false;
    } else {
      this.searchService.tags.splice(
        this.searchService.tags.indexOf(this.tags[index]),
        1,
      );
      if (this.searchService.tags.length === 0) {
        this.checkAllTags = true;
      }
    }
    this.searchService.subjectNext();
  }

  isChecked(index) {
    if (this.searchService.tags.indexOf(this.tags[index]) > -1) {
      return true;
    }
    return false;
  }

  onToggleTags(val, showMoreTags) {
    this.showMoreTags = showMoreTags;
  }

  onClickAssetType(val, index) {
    if (val.target.checked) {
      this.searchService.assetTypes.push(this.assetTypes[index]);
      this.checkAllAssets = false;
    } else {
      this.searchService.assetTypes.splice(
        this.searchService.assetTypes.indexOf(this.assetTypes[index]),
        1,
      );
      if (this.searchService.assetTypes.length === 0) {
        this.checkAllAssets = true;
      }
    }
    this.searchService.subjectNext();
  }

  isAssetChecked(index) {
    if (this.searchService.assetTypes.indexOf(this.assetTypes[index]) > -1) {
      return true;
    }
    return false;
  }

  onToggleAssetType(val, showMoreAssetTypes) {
    this.showMoreAssets = showMoreAssetTypes;
  }

  onClickLanguage(val, index) {
    if (val.target.checked) {
      this.searchService.languages.push(this.languages[index]);
      this.checkAllLanguages = false;
    } else {
      this.searchService.languages.splice(
        this.searchService.languages.indexOf(this.languages[index]),
        1,
      );
      if (this.searchService.languages.length === 0) {
        this.checkAllLanguages = true;
      }
    }
    this.searchService.subjectNext();
  }

  isLanguageChecked(index) {
    if (this.searchService.languages.indexOf(this.languages[index]) > -1) {
      return true;
    }
    return false;
  }

  onToggleLanguages(val, showMoreLanguages) {
    this.showMoreLanguages = showMoreLanguages;
  }

  onClickCountry(val, index) {
    if (val.target.checked) {
      this.searchService.countries.push(this.countries[index]);
      this.checkAllCountries = false;
    } else {
      this.searchService.countries.splice(
        this.searchService.countries.indexOf(this.countries[index]),
        1,
      );
      if (this.searchService.countries.length === 0) {
        this.checkAllCountries = true;
      }
    }
    this.searchService.subjectNext();
  }

  isCountryChecked(index) {
    if (this.searchService.countries.indexOf(this.countries[index]) > -1) {
      return true;
    }
    return false;
  }

  onToggleCountries(val, showMoreCountries) {
    this.showMoreCountries = showMoreCountries;
  }

  onClickAllTags(event) {
    if (event.target.checked) {
      this.searchService.tags = [];
      this.checkAllTags = true;
      this.searchService.subjectNext();

      return true;
    }
    this.searchService.subjectNext();

    return false;
  }

  onClickAllAssets(event) {
    if (event.target.checked) {
      this.searchService.assetTypes = [];
      this.checkAllAssets = true;
      this.searchService.subjectNext();

      return true;
    }
    this.searchService.subjectNext();

    return false;
  }

  onClickAllLanguages(event) {
    if (event.target.checked) {
      this.searchService.languages = [];
      this.checkAllLanguages = true;
      this.searchService.subjectNext();

      return true;
    }
    this.searchService.subjectNext();

    return false;
  }

  onClickAllCountries(event) {
    if (event.target.checked) {
      this.searchService.countries = [];
      this.checkAllCountries = true;
      this.searchService.subjectNext();

      return true;
    }
    this.searchService.subjectNext();

    return false;
  }

  onClickAllTemplates(event) {
    if (event.target.checked) {
      this.searchService.templates = [] as unknown as [
        { id: number; name: string },
      ];
      this.checkAllTemplates = true;
      this.searchService.subjectNext();
      return true;
    }
    return false;
  }

  onClickTemplate(val, index) {
    if (val.target.checked) {
      this.searchService.templates.push(this.templates[index]);
      this.checkAllTemplates = false;
    } else {
      this.searchService.templates.splice(
        this.searchService.templates.indexOf(this.templates[index]),
        1,
      );
      if (this.searchService.templates.length === 0) {
        this.checkAllTemplates = true;
      }
    }
    this.searchService.subjectNext();
  }

  isTemplateChecked(index) {
    if (this.searchService.templates.indexOf(this.templates[index]) > -1) {
      return true;
    }
    return false;
  }

  onToggleTemplates(val, showMoreTemplates) {
    this.showMoreTemplates = showMoreTemplates;
  }

  ngOnDestroy() {
    if (this.tagCreatedSubscription) {
      this.tagCreatedSubscription.unsubscribe();
    }

    if (this.schedulerAsideSubscription) {
      this.schedulerAsideSubscription.unsubscribe();
    }

    if (this.mamCollapsedSubscription) {
      this.mamCollapsedSubscription.unsubscribe();
    }
    if (this.linkedFileUpdated) {
      this.linkedFileUpdated.unsubscribe();
    }
    if (this.searchModeSubscription) {
      this.searchModeSubscription.unsubscribe();
    }
    this.Ob = null;
  }
}
